import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';

function EmailGeneratorModal({ isOpen, onClose, qaItem }) {
  const [emailPurpose, setEmailPurpose] = useState('');
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailKeyPoints, setEmailKeyPoints] = useState('');
  const [emailTone, setEmailTone] = useState('professional');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  // Pre-fill key points with Q&A context when modal opens
  React.useEffect(() => {
    if (isOpen && qaItem) {
      setEmailKeyPoints(`Based on union contract information:\n\nQuestion: ${qaItem.question}\n\nAnswer: ${qaItem.answer}`);
      setResult('');
      setError('');
    }
  }, [isOpen, qaItem]);

  const handleGenerateEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult('');
    setError('');

    try {
      const response = await axios.post(`${API}/ai/generate-email`, {
        purpose: emailPurpose,
        recipient: emailRecipient,
        key_points: emailKeyPoints,
        tone: emailTone,
      });
      setResult(response.data.content);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result);
    alert('Email copied to clipboard!');
  };

  const handleClose = () => {
    setEmailPurpose('');
    setEmailRecipient('');
    setEmailKeyPoints('');
    setEmailTone('professional');
    setResult('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content email-generator-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📧 Generate Email</h2>
          <button className="modal-close" onClick={handleClose}>&times;</button>
        </div>

        <div className="email-generator-body">
          {!result ? (
            <form onSubmit={handleGenerateEmail}>
              {error && <div className="error-message">{error}</div>}
              
              <div className="form-group">
                <label className="form-label">Purpose of Email</label>
                <input
                  type="text"
                  className="form-input"
                  value={emailPurpose}
                  onChange={(e) => setEmailPurpose(e.target.value)}
                  placeholder="e.g., Request clarification on overtime policy"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Recipient</label>
                <input
                  type="text"
                  className="form-input"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  placeholder="e.g., Manager, HR Department, Union Rep"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Key Points to Include</label>
                <textarea
                  className="form-textarea"
                  value={emailKeyPoints}
                  onChange={(e) => setEmailKeyPoints(e.target.value)}
                  placeholder="List the main points you want to communicate..."
                  rows="6"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Tone</label>
                <select
                  className="form-select"
                  value={emailTone}
                  onChange={(e) => setEmailTone(e.target.value)}
                >
                  <option value="professional">Professional</option>
                  <option value="formal">Formal</option>
                  <option value="friendly">Friendly</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-generate"
                  disabled={loading}
                >
                  {loading ? '✨ Generating...' : '✨ Generate Email'}
                </button>
              </div>
            </form>
          ) : (
            <div className="email-result-container">
              <div className="email-result-header">
                <h3>Generated Email</h3>
              </div>
              <div className="email-result-content">
                <pre>{result}</pre>
              </div>
              <div className="email-result-actions">
                <button className="btn-copy" onClick={copyToClipboard}>
                  📋 Copy to Clipboard
                </button>
                <button className="btn-regenerate" onClick={() => setResult('')}>
                  🔄 Edit & Regenerate
                </button>
                <button className="btn-done" onClick={handleClose}>
                  ✓ Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmailGeneratorModal;
